import React, { useState, useEffect, useCallback } from "react";
import { History, HistorySearchItem, HistorySort, HistoryStatus, AppConfig } from "../types";
import { DropCatchService } from "../services/dropcatchService";

interface HistoryViewProps {
  config: AppConfig;
  dropcatchService: DropCatchService;
}

const HistoryView: React.FC<HistoryViewProps> = ({ dropcatchService }) => {
  const [activeTab, setActiveTab] = useState<"auctions" | "backorders">("auctions");
  const [auctionHistory, setAuctionHistory] = useState<History[]>([]);
  const [backorderHistory, setBackorderHistory] = useState<HistorySearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<HistorySort>("UpdatedDesc");

  const loadHistory = useCallback(async () => {
    if (!dropcatchService.isConfigured()) {
      setError("Please configure your DropCatch API credentials");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (activeTab === "auctions") {
        const result = await dropcatchService.getAuctionHistory({
          searchTerm: searchTerm || undefined,
          sort: sortBy,
          size: 50,
        });
        setAuctionHistory(result.items);
      } else {
        const result = await dropcatchService.getBackorderHistory({
          searchTerm: searchTerm || undefined,
          sort: sortBy,
          size: 50,
        });
        setBackorderHistory(result.items);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [dropcatchService, activeTab, searchTerm, sortBy]);

  useEffect(() => {
    if (dropcatchService.isConfigured()) {
      loadHistory();
    }
  }, [activeTab]);

  const getStatusStyle = (status: HistoryStatus) => {
    switch (status) {
      case "AuctionWon":
      case "Successful":
      case "Purchased":
        return "border-green-500 text-green-400";
      case "AuctionLost":
      case "Missed":
      case "LostPreemptDiscount":
      case "LostPreemptPartner":
      case "LostPreemptStandard":
        return "border-green-700 text-green-600";
      case "InAuction":
        return "border-green-500 text-green-400 animate-pulse";
      case "Suspended":
        return "border-green-800 text-green-700";
      default:
        return "border-green-700 text-green-600";
    }
  };

  const getStatusLabel = (status: HistoryStatus) => {
    switch (status) {
      case "AuctionWon":
        return "[WON]";
      case "AuctionLost":
        return "[LOST]";
      case "Successful":
        return "[SUCCESS]";
      case "InAuction":
        return "[ACTIVE]";
      case "Missed":
        return "[MISSED]";
      case "LostPreemptDiscount":
      case "LostPreemptPartner":
      case "LostPreemptStandard":
        return "[PREEMPT]";
      case "Purchased":
        return "[BOUGHT]";
      case "Suspended":
        return "[SUSPEND]";
      default:
        return `[${String(status).toUpperCase()}]`;
    }
  };

  // Calculate stats
  const wonAuctions = auctionHistory.filter(
    (h) => h.status === "AuctionWon" || h.status === "Successful"
  );
  const lostAuctions = auctionHistory.filter(
    (h) => h.status === "AuctionLost" || h.status === "Missed"
  );
  const totalSpent = wonAuctions.reduce((sum, h) => sum + h.amount, 0);

  return (
    <div className="p-6 space-y-6 font-mono">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1
            className="text-2xl font-bold text-green-400 tracking-wider"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {">"} HISTORY.LOG
          </h1>
          <p className="text-green-600 text-sm mt-1">
            REVIEW PAST AUCTION AND BACKORDER ACTIVITY
          </p>
        </div>
        <button
          onClick={loadHistory}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-black font-bold tracking-wider hover:bg-green-400 transition-colors disabled:opacity-50 flex items-center gap-2"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.3)" }}
        >
          {loading ? (
            <span className="animate-pulse">LOADING...</span>
          ) : (
            "[REFRESH]"
          )}
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">DOMAINS_WON</div>
          <div
            className="text-3xl font-bold text-green-400"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {wonAuctions.length}
          </div>
          <div className="text-green-700 text-xs mt-1">ACQUIRED</div>
        </div>

        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">LOST_MISSED</div>
          <div
            className="text-3xl font-bold text-green-500"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {lostAuctions.length}
          </div>
          <div className="text-green-700 text-xs mt-1">FAILED</div>
        </div>

        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">TOTAL_SPENT</div>
          <div
            className="text-3xl font-bold text-green-400"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            ${totalSpent.toLocaleString()}
          </div>
          <div className="text-green-700 text-xs mt-1">USD</div>
        </div>

        <div
          className="bg-black border-2 border-green-700 p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="text-green-600 text-xs mb-1">WIN_RATE</div>
          <div
            className="text-3xl font-bold text-green-400"
            style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
          >
            {wonAuctions.length + lostAuctions.length > 0
              ? Math.round((wonAuctions.length / (wonAuctions.length + lostAuctions.length)) * 100)
              : 0}%
          </div>
          <div className="text-green-700 text-xs mt-1">SUCCESS</div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-black border-2 border-green-500 p-4 flex items-start gap-3">
          <span className="text-green-500">[ERR!]</span>
          <p className="text-green-400 flex-1">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-green-600 hover:text-green-400"
          >
            [X]
          </button>
        </div>
      )}

      {/* Tabs & Filters */}
      <div
        className="bg-black border-2 border-green-700 p-4"
        style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
      >
        <div className="flex flex-col md:flex-row gap-4 items-center">
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("auctions")}
              className={`px-3 py-1 border-2 transition-colors tracking-wider text-sm ${
                activeTab === "auctions"
                  ? "border-green-500 bg-green-500 text-black"
                  : "border-green-700 text-green-600 hover:border-green-500"
              }`}
            >
              [AUCTIONS]
            </button>
            <button
              onClick={() => setActiveTab("backorders")}
              className={`px-3 py-1 border-2 transition-colors tracking-wider text-sm ${
                activeTab === "backorders"
                  ? "border-green-500 bg-green-500 text-black"
                  : "border-green-700 text-green-600 hover:border-green-500"
              }`}
            >
              [BACKORDERS]
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-2 text-green-600">{">"}</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="SEARCH HISTORY..."
              className="flex-1 px-2 py-2 bg-transparent text-green-400 outline-none text-sm placeholder-green-800"
              onKeyDown={(e) => e.key === "Enter" && loadHistory()}
            />
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as HistorySort)}
            className="bg-black border-2 border-green-700 text-green-400 px-3 py-2 focus:border-green-500 outline-none text-sm"
          >
            <option value="UpdatedDesc">MOST RECENT</option>
            <option value="UpdatedAsc">OLDEST FIRST</option>
            <option value="AmountDesc">HIGHEST AMOUNT</option>
            <option value="AmountAsc">LOWEST AMOUNT</option>
            <option value="DomainAsc">DOMAIN A-Z</option>
          </select>

          <button
            onClick={loadHistory}
            className="px-4 py-2 border-2 border-green-700 text-green-600 hover:border-green-500 hover:text-green-400 transition-colors tracking-wider text-sm"
          >
            [SEARCH]
          </button>
        </div>
      </div>

      {/* Auction History Table */}
      {activeTab === "auctions" && (
        <div
          className="bg-black border-2 border-green-700 overflow-hidden"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-green-700">
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    DOMAIN
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    AMOUNT
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    BIDDERS
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    DATE
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-green-900">
                {auctionHistory.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <pre className="text-green-700 text-xs mb-4">
{`    ____________________
   |                    |
   |  NO AUCTION DATA   |
   |____________________|
   |____________________|`}
                      </pre>
                      <p className="text-green-600 text-sm">
                        YOUR PAST AUCTIONS WILL APPEAR HERE
                      </p>
                    </td>
                  </tr>
                ) : (
                  auctionHistory.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-green-900/20 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span
                          className="font-bold text-green-400"
                          style={{ textShadow: "0 0 5px rgba(34, 197, 94, 0.3)" }}
                        >
                          {item.domain}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 border text-xs font-bold tracking-wider ${getStatusStyle(
                            item.status
                          )}`}
                        >
                          {getStatusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="font-bold text-green-400"
                          style={{ textShadow: "0 0 5px rgba(34, 197, 94, 0.3)" }}
                        >
                          ${item.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-green-500">{item.bidders?.length || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-green-600 text-sm">
                          {new Date(item.updatedDate).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="border-t-2 border-green-700 px-4 py-2 text-xs text-green-700 flex justify-between">
            <span>RECORDS: {auctionHistory.length}</span>
            <span className="animate-pulse">AUCTION_LOG READY_</span>
          </div>
        </div>
      )}

      {/* Backorder History Table */}
      {activeTab === "backorders" && (
        <div
          className="bg-black border-2 border-green-700 overflow-hidden"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.1)" }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-green-700">
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    DOMAIN
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    TYPE
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    RESULT
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    AMOUNT
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-green-500 uppercase tracking-wider">
                    DATE
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-green-900">
                {backorderHistory.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <pre className="text-green-700 text-xs mb-4">
{`    ____________________
   |                    |
   | NO BACKORDER DATA  |
   |____________________|
   |____________________|`}
                      </pre>
                      <p className="text-green-600 text-sm">
                        YOUR PAST BACKORDERS WILL APPEAR HERE
                      </p>
                    </td>
                  </tr>
                ) : (
                  backorderHistory.map((item) => (
                    <tr
                      key={item.referenceId}
                      className="hover:bg-green-900/20 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span
                          className="font-bold text-green-400"
                          style={{ textShadow: "0 0 5px rgba(34, 197, 94, 0.3)" }}
                        >
                          {item.domain}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 border text-xs font-bold tracking-wider ${
                            item.type === "Auction"
                              ? "border-green-500 text-green-400"
                              : "border-green-600 text-green-500"
                          }`}
                        >
                          [{item.type.toUpperCase()}]
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-green-500">{item.result || "-"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="font-bold text-green-400"
                          style={{ textShadow: "0 0 5px rgba(34, 197, 94, 0.3)" }}
                        >
                          ${item.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-green-600 text-sm">
                          {new Date(item.date).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="border-t-2 border-green-700 px-4 py-2 text-xs text-green-700 flex justify-between">
            <span>RECORDS: {backorderHistory.length}</span>
            <span className="animate-pulse">BACKORDER_LOG READY_</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryView;
